import React from 'react';
import { Modal, Descriptions, Tag } from 'antd';
import { ActivityLog } from './ActivityLog.types';
import { DEPARTMENT_CONFIG, USER_TYPE_CONFIG, STATUS_CONFIG, formatDateTime, getIcon } from './ActivityLogSampleData';

interface LogDetailModalProps {
  log: ActivityLog | null;
  open: boolean;
  onClose: () => void;
}

const LogDetailModal: React.FC<LogDetailModalProps> = ({ log, open, onClose }) => {
  if (!log) return null;

  const deptCfg = DEPARTMENT_CONFIG[log.department] ?? {
    iconKey: 'export',
    color: '#533AB7',
    bg: '#EEEDFE',
    label: log.department ?? 'Unknown',
  };
  const userCfg = USER_TYPE_CONFIG[log.userType] ?? {
    iconKey: 'user',
    color: '#185FA5',
    bg: '#E6F1FB',
    label: log.userType ?? 'Unknown',
  };
  const statusCfg = STATUS_CONFIG[log.status];
  const { date, time } = formatDateTime(log.timestamp);

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
    fontSize: 13,
  };
  const labelStyle: React.CSSProperties = {
    color: '#888',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    width: 120,
    paddingTop: 1,
  };
  const valueStyle: React.CSSProperties = {
    color: '#222',
    fontWeight: 500,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    flex: 1,
    minWidth: 0,
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={650}
      style={{ maxWidth: '100%' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: deptCfg.bg,
              flexShrink: 0,
            }}
          >
            {getIcon(deptCfg.iconKey, { color: deptCfg.color, fontSize: 16 })}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3, wordBreak: 'break-word' }}>{log.action}</div>
            <div style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>
              {log.department} · {date} at {time}
            </div>
          </div>
        </div>
      }
    >
      <div style={{ width: '100%', boxSizing: 'border-box' }}>
        {/* Status badge */}
        <div style={{ marginBottom: 16 }}>
          <Tag
            style={{
              background: statusCfg.bg,
              color: statusCfg.color,
              border: 'none',
              fontWeight: 500,
              borderRadius: 6,
              padding: '2px 10px',
            }}
          >
            {statusCfg.label}
          </Tag>
        </div>

        {/* Description */}
        <div
          style={{
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            color: '#444',
            marginBottom: 20,
            lineHeight: 1.7,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {log.description}
        </div>

        {/* Core info — plain flex rows instead of AntD Descriptions */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
            <div style={rowStyle}>
              <span style={labelStyle}>Performed by</span>
              <span style={valueStyle}>{log.userName}</span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>User type</span>
              <span style={{ ...valueStyle, display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: userCfg.bg,
                    color: userCfg.color,
                    borderRadius: 6,
                    padding: '1px 8px',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {getIcon(userCfg.iconKey, { fontSize: 12 })}
                  {userCfg.label}
                </span>
              </span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Department</span>
              <span style={{ ...valueStyle, display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: deptCfg.bg,
                    color: deptCfg.color,
                    borderRadius: 6,
                    padding: '1px 8px',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {getIcon(deptCfg.iconKey, { fontSize: 12 })}
                  {deptCfg.label}
                </span>
              </span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Affected entity</span>
              <span style={valueStyle}>
                <code
                  style={{
                    fontSize: 12,
                    background: '#f5f5f5',
                    padding: '1px 6px',
                    borderRadius: 4,
                    wordBreak: 'break-all',
                  }}
                >
                  {log.affectedEntity || '—'}
                </code>
              </span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Date</span>
              <span style={valueStyle}>{date}</span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Time</span>
              <span style={valueStyle}>{time}</span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>IP address</span>
              <span style={valueStyle}>
                <code style={{ fontSize: 12, background: '#f5f5f5', padding: '1px 6px', borderRadius: 4 }}>
                  {log.ipAddress || '—'}
                </code>
              </span>
            </div>

            <div style={rowStyle}>
              <span style={labelStyle}>Session ID</span>
              <span style={valueStyle}>
                <code
                  style={{
                    fontSize: 12,
                    background: '#f5f5f5',
                    padding: '1px 6px',
                    borderRadius: 4,
                    wordBreak: 'break-all',
                  }}
                >
                  {log.sessionId || '—'}
                </code>
              </span>
            </div>
          </div>
        </div>

        {/* Extra metadata */}
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <>
            <div
              style={{
                fontWeight: 500,
                fontSize: 13,
                color: '#888',
                margin: '16px 0 8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Additional Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
              {Object.entries(log.metadata).map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    background: '#fafafa',
                    borderRadius: 8,
                    padding: '8px 12px',
                    border: '1px solid #f0f0f0',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>{k}</div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#333',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {String(v)}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LogDetailModal;
